import { FieldTranslator } from '../FieldTranslator';
import { TaskManager } from '../TaskManager';
import { BaseResourceManager } from '../BaseResourceManager';
import { AreaField, ResoResource } from '../../system/reso';
import { useReso } from '../../contexts/ResoContext';
import { ResourceManagerOptions } from '../types';

// Field name mapping for PropertyArea
const FIELD_MAPPING: Record<string, string> = {
  [AreaField.KEY]: 'PROP_AREA_KEY',
  [AreaField.TYPE]: 'PROP_AREA_TYPE',
  [AreaField.COUNTY]: 'PROP_AREA_COUNTY',
  [AreaField.STATE]: 'PROP_AREA_STATE',
  [AreaField.MODIFICATION_TIMESTAMP]: 'PROP_AREA_MODIFICATION_TIMESTAMP',
};

export class AreaResource implements Record<string, any> {
  [key: string]: any;

  constructor(data: Record<string, any> = {}) {
    // Copy all provided data to this instance
    Object.assign(this, data);
  }

  getKey(): string | number | undefined {
    return this[FIELD_MAPPING[AreaField.KEY]];
  }

  getItemNumber(): number | undefined {
    return undefined; // Areas don't have an item number
  }

  getListingKeyField(): string | undefined {
    return undefined; // Areas are not directly related to listings
  }

  getListingKey(): string | undefined {
    return undefined; // Areas are not directly related to listings
  }

  // Utility methods for area resources
  getType(): string | undefined {
    return this[FIELD_MAPPING[AreaField.TYPE]];
  }

  getCounty(): string | undefined {
    return this[FIELD_MAPPING[AreaField.COUNTY]];
  }

  getState(): string | undefined {
    return this[FIELD_MAPPING[AreaField.STATE]];
  }
}

export class AreaResourceManager extends BaseResourceManager<AreaResource> {
  constructor(
    executeQuery: ReturnType<typeof useReso>['executeQuery'],
    fieldTranslator: FieldTranslator,
    taskManager: TaskManager,
    options: ResourceManagerOptions = {},
  ) {
    super(ResoResource.AREA, executeQuery, fieldTranslator, taskManager, {
      ...options,
      useTranslation: false, // Disable translation for PropertyArea
    });
  }

  /**
   * Map field name using our custom mapping for PropertyArea
   */
  protected mapFieldName(field: string): string {
    return FIELD_MAPPING[field] || field;
  }

  /**
   * Core mapping function from API data to AreaResource
   */
  protected mapFromApiCore(apiData: any): AreaResource {
    return new AreaResource(apiData);
  }

  /**
   * Core mapping function from AreaResource to API data
   */
  protected mapToApiCore(resource: AreaResource): any {
    // Return a clean copy of the resource
    return { ...resource };
  }

  /**
   * Find areas by county
   */
  public async findByCounty(county: string): Promise<AreaResource[]> {
    return this.findListByField(AreaField.COUNTY, county);
  }

  /**
   * Find areas by state
   */
  public async findByState(state: string): Promise<AreaResource[]> {
    return this.findListByField(AreaField.STATE, state);
  }

  /**
   * Find areas by type
   */
  public async findByType(type: string): Promise<AreaResource[]> {
    return this.findListByField(AreaField.TYPE, type);
  }

  /**
   * Find areas by state and county
   */
  public async findByStateAndCounty(
    state: string,
    county: string,
  ): Promise<AreaResource[]> {
    return this.findListByFields({
      [AreaField.STATE]: state,
      [AreaField.COUNTY]: county,
    });
  }

  /**
   * Override findListByField to use our field mapping
   */
  public async findListByField(
    field: string,
    value: any,
  ): Promise<AreaResource[]> {
    const mappedField = this.mapFieldName(field);
    return super.findListByField(mappedField, value);
  }

  /**
   * Override findByField to use our field mapping
   */
  public async findByField(
    field: string,
    value: any,
  ): Promise<AreaResource | null> {
    const mappedField = this.mapFieldName(field);
    return super.findByField(mappedField, value);
  }
}

/**
 * Create a new AreaResource instance with required fields
 */
export function createAreaResource(
  data: Partial<Record<string, any>> = {},
): AreaResource {
  const defaults = {};

  return new AreaResource({
    ...defaults,
    ...data,
  });
}
